/**
 * As mensagens que pararam na fila — o bloco de tipo `fila` (#443, §4.3).
 *
 * O que este bloco existe para dizer é **há quanto tempo** a fila parou: é o tempo, e não a
 * contagem, que separa lentidão de incidente. Por isso ele é o número grande; pendentes e
 * falhadas ficam abaixo, como a composição do total.
 *
 * O vocabulário visual é o dos vizinhos (`AttendanceFunnel`, `ScheduleOccupancyCard`): título em
 * `h6` com o ícone do assunto, corpo em duas linhas rótulo/valor, e uma única ação, no rodapé.
 */
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import { HourglassBottom } from '@mui/icons-material';

import type { FilaParada } from '../../../types/dashboard';

interface FilaDeMensagensProps {
  rotulo: string;
  data: FilaParada;
  onVerFila?: () => void;
}

const LinhaDaFila: React.FC<{ rotulo: string; valor: number }> = ({ rotulo, valor }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {rotulo}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {valor}
    </Typography>
  </Box>
);

const FilaDeMensagens: React.FC<FilaDeMensagensProps> = ({ rotulo, data, onVerFila }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <HourglassBottom color="warning" sx={{ fontSize: 24 }} />
        <Typography variant="h6">{rotulo}</Typography>
      </Box>

      <Typography variant="h4" fontWeight={600} color="warning.main">
        {data.total}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        parada há {data.paradaHaMinutos} minutos
      </Typography>

      <Divider sx={{ mb: 1 }} />

      <LinhaDaFila rotulo="Pendentes" valor={data.pendentes} />
      <LinhaDaFila rotulo="Falhadas" valor={data.falhadas} />
    </CardContent>

    {onVerFila && (
      <CardActions>
        <Button size="small" onClick={onVerFila}>
          Ver a fila
        </Button>
      </CardActions>
    )}
  </Card>
);

export default FilaDeMensagens;
